import React, { useState } from 'react';
import { FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import SEOHelmet from '../components/SEOHelmet';
import { metaTags } from '../components/seoConfig';
import { Images } from '../utils/cloudflareImages';
import { Blob } from '../components/Blob';
import { BackgroundVideo } from '../components/BackgroundVideo';

interface GalleryImage {
  id: number;
  title: string;
  category: string;
  description: string;
  image: string;
}

// Gallery images using Cloudflare CDN URLs
const galleryImages: GalleryImage[] = [
  {
    id: 1,
    title: 'Classic Box Braids',
    category: 'Box Braids',
    description: 'Beautiful classic box braids with perfect tension',
    image: Images.gallery.classicBoxBraids,
  },
  {
    id: 2,
    title: 'Goddess Braids',
    category: 'Goddess',
    description: 'Luxurious goddess braids with gold accents',
    image: Images.gallery.goddessBraids,
  },
  {
    id: 3,
    title: 'Cornrow Design',
    category: 'Cornrows',
    description: 'Intricate cornrow patterns for a stunning look',
    image: Images.gallery.cornrowDesign,
  },
  {
    id: 4,
    title: 'Two-Strand Twists',
    category: 'Twists',
    description: 'Elegant twists with perfect symmetry',
    image: Images.gallery.twoStrandTwists,
  },
  {
    id: 5,
    title: 'Feed-In Braids',
    category: 'Box Braids',
    description: 'Seamless feed-in braids that look natural',
    image: Images.gallery.feedInBraids,
  },
  {
    id: 6,
    title: 'Colored Braids',
    category: 'Specialty',
    description: 'Creative colored braids with vibrant hues',
    image: Images.gallery.coloredBraids,
  },
  {
    id: 7,
    title: 'Protective Style',
    category: 'Box Braids',
    description: 'Perfect protective styling for hair health',
    image: Images.gallery.protectiveStyle,
  },
  {
    id: 8,
    title: 'Festival Braids',
    category: 'Specialty',
    description: 'Fun festival-ready braided style',
    image: Images.gallery.festivalBraids,
  },
  {
    id: 9,
    title: 'Bridal Braids',
    category: 'Specialty',
    description: 'Elegant braided updo for special occasions',
    image: Images.gallery.bridalBraids,
  },
];

const categories = [
  'All',
  'Box Braids',
  'Cornrows',
  'Twists',
  'Goddess',
  'Specialty',
];

export const Gallery: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const filteredImages =
    selectedCategory === 'All'
      ? galleryImages
      : galleryImages.filter((img) => img.category === selectedCategory);

  const openLightbox = (image: GalleryImage) => {
    setSelectedImage(image);
    const index = filteredImages.findIndex((img) => img.id === image.id);
    setCurrentImageIndex(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const nextImage = () => {
    const newIndex = (currentImageIndex + 1) % filteredImages.length;
    setCurrentImageIndex(newIndex);
    setSelectedImage(filteredImages[newIndex]);
  };

  const prevImage = () => {
    const newIndex =
      (currentImageIndex - 1 + filteredImages.length) % filteredImages.length;
    setCurrentImageIndex(newIndex);
    setSelectedImage(filteredImages[newIndex]);
  };

  return (
    <>
      <SEOHelmet
        title={metaTags.gallery.title}
        description={metaTags.gallery.description}
        keywords={metaTags.gallery.keywords}
      />

      {/* Hero Section - Minimalist */}
      <motion.section
        className="pt-32 pb-20 px-6 md:px-20 relative overflow-hidden min-h-[70vh] flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        {/* Background Video */}
        <BackgroundVideo
          videoId="467ef9ea18ef4abbac73a6d12c069ad8"
          overlayOpacity={40}
        />

        <div className="max-w-6xl mx-auto text-center space-y-6 relative z-10">
          <motion.p
            className="text-sm tracking-widest uppercase text-white/90"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Our Work
          </motion.p>
          <motion.h1
            className="font-serif text-6xl md:text-7xl text-white drop-shadow-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Gallery
          </motion.h1>
          <motion.p
            className="text-xl text-white/90 max-w-2xl mx-auto leading-relaxed drop-shadow-md"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            Explore our portfolio of professional braiding work
          </motion.p>
        </div>
      </motion.section>

      {/* Main Gallery Section */}
      <section className="py-24 px-6 md:px-20 bg-[#FAF8F4] relative overflow-hidden">
        <Blob
          variant={2}
          className="-z-10 right-0 top-20 w-[500px] h-[500px]"
          style={{ opacity: 0.04 }}
        />
        <Blob
          variant={4}
          className="-z-10 left-10 bottom-40 w-96 h-96"
          style={{ opacity: 0.05 }}
        />
        <div className="max-w-7xl mx-auto">
          {/* Category Filter - Simplified */}
          <div className="flex flex-wrap justify-center gap-8 mb-20">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`text-sm tracking-widest uppercase transition-colors pb-1 ${
                  selectedCategory === category
                    ? 'text-[#0B0B0B] border-b-2 border-[#C97B63]'
                    : 'text-[#6B6970] hover:text-[#0B0B0B]'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Gallery Grid - Pinterest Style with Framed Cards */}
          <motion.div
            key={selectedCategory}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.1,
                },
              },
            }}
          >
            {filteredImages.map((image, index) => (
              <motion.article
                key={image.id}
                className="relative bg-white p-8 rounded-2xl cursor-pointer"
                onClick={() => openLightbox(image)}
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: { opacity: 1, y: 0 },
                }}
                whileHover={{ y: -4 }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex flex-col gap-6">
                  {/* Framed Image */}
                  <div className="relative p-6 border-2 border-[#0B0B0B] rounded-xl">
                    <div className="w-full aspect-[4/3] bg-gray-100 rounded-md overflow-hidden">
                      <img
                        src={image.image}
                        alt={image.title}
                        loading="lazy"
                        className="object-cover w-full h-full"
                      />
                    </div>
                    {/* Category Tag */}
                    <div className="absolute top-3 left-3 bg-white/90 border border-black/10 px-3 py-1 rounded-md text-xs font-medium">
                      {image.category}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex flex-col gap-2">
                    <h3 className="font-serif text-2xl leading-tight text-[#0B0B0B]">
                      {image.title}
                    </h3>
                    <p className="text-sm text-[#6B6970] leading-relaxed">
                      {image.description}
                    </p>
                  </div>
                </div>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
            onClick={closeLightbox}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div
              className="relative max-w-6xl w-full h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              {/* Close Button */}
              <button
                onClick={closeLightbox}
                className="absolute top-4 right-4 z-20 bg-white hover:bg-gray-100 rounded-2xl p-3 transition-colors shadow-lg"
                aria-label="Close gallery"
              >
                <FiX size={24} className="text-[#0B0B0B]" />
              </button>

              {/* Previous Button */}
              <button
                onClick={prevImage}
                className="absolute left-4 z-20 bg-white/90 hover:bg-white p-3 rounded-2xl transition-colors shadow-lg"
                aria-label="Previous image"
              >
                <FiChevronLeft size={32} className="text-[#0B0B0B]" />
              </button>

              {/* Next Button */}
              <button
                onClick={nextImage}
                className="absolute right-4 z-20 bg-white/90 hover:bg-white p-3 rounded-2xl transition-colors shadow-lg"
                aria-label="Next image"
              >
                <FiChevronRight size={32} className="text-[#0B0B0B]" />
              </button>

              {/* Image Container */}
              <div className="w-full h-full flex flex-col items-center justify-center p-16">
                {/* Image with proper containment */}
                <div className="relative w-full h-[calc(100vh-250px)] flex items-center justify-center mb-6">
                  <img
                    src={selectedImage.image}
                    alt={selectedImage.title}
                    className="max-w-full max-h-full w-auto h-auto object-contain shadow-2xl rounded-xl"
                  />
                </div>

                {/* Info */}
                <div className="bg-white rounded-2xl p-6 max-w-2xl w-full">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h2 className="font-serif text-2xl text-[#0B0B0B] mb-2">
                        {selectedImage.title}
                      </h2>
                      <p className="text-[#6B6970]">
                        {selectedImage.description}
                      </p>
                    </div>
                    <span className="inline-block px-4 py-2 bg-gray-100 text-[#0B0B0B] rounded-md text-xs font-medium tracking-wider">
                      {selectedImage.category}
                    </span>
                  </div>
                  <p className="text-sm text-[#6B6970] mt-4">
                    Image {currentImageIndex + 1} of {filteredImages.length}
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA Section - Simplified */}
      <motion.section
        className="py-32 px-6 md:px-20 bg-white"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-100px' }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="font-serif text-5xl md:text-6xl text-[#0B0B0B]">
            Ready to book?
          </h2>
          <p className="text-xl text-[#6B6970]">
            Let's create your perfect style
          </p>
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <a
              href="/contact"
              className="inline-block px-10 py-4 bg-[#0B0B0B] text-white rounded-2xl font-medium hover:shadow-lg transition-all"
            >
              Schedule Appointment
            </a>
          </motion.div>
        </div>
      </motion.section>
    </>
  );
};

export default Gallery;
